import React from 'react';

interface NonSkCountryStepProps {
  countries: any[];
  countriesLoading: boolean;
  selectedCountry: string;
  onCountryChange: (country: string) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  isProcessing: boolean;
  statusMessage: string;
  onSubmit: (country: string, file: File) => void;
  onCancel: () => void;
}

const NonSkCountryStep: React.FC<NonSkCountryStepProps> = ({
  countries,
  countriesLoading,
  selectedCountry,
  onCountryChange,
  file,
  onFileChange,
  isProcessing,
  statusMessage,
  onSubmit,
  onCancel
}) => {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    onFileChange(selectedFile || null);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (selectedCountry && file) {
      onSubmit(selectedCountry, file);
    }
  };

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: '#667eea',
      padding: '20px',
      minHeight: '100vh'
    }}>
      <div style={{
        width: 'min(720px, 100%)',
        margin: '0 auto',
        background: '#fff',
        borderRadius: '14px',
        padding: 'clamp(16px, 4vw, 28px)',
        boxShadow: '0 4px 12px rgba(0,0,0,.08)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '22px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: '#f59e0b',
            display: 'grid',
            placeItems: 'center',
            margin: '0 auto 14px'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ width: '30px', height: '30px', stroke: '#fff', strokeWidth: 2, fill: 'none' }}>
              <circle cx="12" cy="12" r="10"/>
              <path d="M2 12h20"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
          </div>
          <h1 style={{ fontSize: 'clamp(22px, 6vw, 30px)', margin: '0 0 6px', color: '#111827' }}>
            Neslovenský bloček
          </h1>
          <p style={{ color: '#6b7280', fontSize: 'clamp(15px, 4vw, 17px)', lineHeight: '1.5' }}>
            Pre spracovanie zahraničného bločka, vyberte krajinu odkiaľ bloček pochádza a nahrajte fotografiu celého bločka, tak aby boli viditeľné a čitateľné položky a sumy. Spracovanie bločka môže trvať niekoľko desiatok sekúnd (20-30s), prečítané údaje z bločka dostanete následne na kontrolu správnosti.
            <br /><br />
            Ak máte bloček z krajiny, ktorá nie je v zozname dajte nám vedieť.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Country Selection */}
          <div style={{ margin: '20px 0' }}>
            <label style={{
              display: 'block',
              fontWeight: 600,
              marginBottom: '8px',
              color: '#111827',
              fontSize: 'clamp(16px, 4.5vw, 18px)'
            }}>
              Krajina pôvodu bločka
            </label>
            <select
              value={selectedCountry}
              onChange={(e) => onCountryChange(e.target.value)}
              required
              disabled={countriesLoading}
              style={{
                width: '100%',
                padding: '14px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '17px',
                background: countriesLoading ? '#f9fafb' : '#fff',
                color: countriesLoading ? '#6b7280' : '#111827',
                fontFamily: 'inherit'
              }}
            >
              <option value="">{countriesLoading ? "-- Načítavam krajiny --" : "-- Vyberte krajinu --"}</option>
              {countries.map(country => {
                const flagEmojis: { [key: string]: string } = {
                  'AT': '🇦🇹',
                  'CZ': '🇨🇿',
                  'DE': '🇩🇪',
                  'PL': '🇵🇱',
                  'PT': '🇵🇹'
                };
                const flag = flagEmojis[country.country_code] || '🌍';
                return (
                  <option key={country.country_code} value={country.country_code}>
                    {flag} {country.country_name}
                  </option>
                );
              })}
            </select>
          </div>

          {/* File Upload */}
          <div style={{ margin: '20px 0' }}>
            <label style={{
              display: 'block',
              fontWeight: 600,
              marginBottom: '8px',
              color: '#111827',
              fontSize: 'clamp(16px, 4.5vw, 18px)'
            }}>
              Fotografia bločka
            </label>
            <div
              style={{
                border: '2px dashed #e5e7eb',
                borderRadius: '12px',
                padding: 'clamp(18px, 5vw, 28px)',
                textAlign: 'center',
                background: '#f9fafb',
                cursor: 'pointer',
                transition: 'border-color .2s, background .2s',
                borderColor: file ? '#10b981' : '#e5e7eb',
                backgroundColor: file ? '#f0fdf4' : '#f9fafb'
              }}
              onClick={() => document.getElementById('nonSkFileInput')?.click()}
            >
              <input
                type="file"
                id="nonSkFileInput"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                required
                aria-label="Nahrať obrázok neslovenského bločku"
              />
              {!file && (
                <svg style={{ width: '44px', height: '44px', margin: '0 auto 8px', stroke: '#9ca3af' }}
                     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round"
                        d="M7 16a4 4 0 0 1-.88-7.903A5 5 0 1 1 15.9 6L16 6a5 5 0 0 1 1 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                </svg>
              )}
              <div style={{
                color: file ? '#065f46' : '#4b5563',
                fontSize: 'clamp(16px, 4.5vw, 18px)',
                fontWeight: file ? 700 : 'normal'
              }}>
                {file ? `✅ ${file.name}` : (
                  <>
                    Kliknite pre výber súboru
                    <small style={{ display: 'block', color: '#9ca3af', fontSize: '14px', marginTop: '4px' }}>
                      Fotogaléria • Fotoaparát • Súbory
                    </small>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Status Display */}
          {isProcessing && (
            <div style={{
              background: '#eef2ff',
              border: '1px solid #667eea',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{
                color: '#667eea',
                fontSize: '16px',
                fontWeight: 600,
                marginBottom: '8px'
              }}>
                {statusMessage}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isProcessing}
            style={{
              width: '100%',
              minHeight: '52px',
              background: isProcessing ? '#cbd5e1' : '#f59e0b',
              color: '#fff',
              border: 'none',
              borderRadius: '14px',
              fontSize: '19px',
              fontWeight: 700,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              marginBottom: '12px'
            }}
          >
            {isProcessing ? '⏳ Analyzujem bloček...' : '📄 Spracuj bloček'}
          </button>

          {/* Back Button */}
          <button
            type="button"
            onClick={onCancel}
            style={{
              width: '100%',
              minHeight: '52px',
              background: '#f3f4f6',
              color: '#374151',
              border: '2px solid #d1d5db',
              borderRadius: '14px',
              fontSize: '19px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            ← Späť na skener
          </button>
        </form>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '18px',
          color: '#9ca3af',
          fontSize: '14px'
        }}>
          V4 • 🌍 Neslovenský bloček • smartblocek.sk byTomasko
        </div>
      </div>
    </div>
  );
};

export default NonSkCountryStep;
